import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";

import { companiesApi, usersApi } from "../../api";
import { useAuth } from "../../context/AuthContext";
import { useSubmitLock } from "../../hooks/useSubmitLock";
import { avatarInitials } from "../../utils/avatarInitials";
import { NotificationPreferencesCard } from "../../components/NotificationPreferencesCard";
import { toE164 } from "../../utils/phone";
import type { TicketTemplateId } from "../../types/rsvp";
import { invoiceInfoFormatError, missingInvoiceInfo } from "../../utils/companyInvoiceInfo";
import {
  getCompanyDefaultTemplateIdLocal,
  setCompanyDefaultTemplateIdLocal,
} from "../../utils/ticketTemplateStore";
import { ChangePasswordModal } from "./ChangePasswordModal";
import DefaultTemplatePreview from "./DefaultTemplatePreview";
import { useSectionEditToggle } from "./useSectionEditToggle";
import "../../components/ticketTemplate/ticketTemplate.css";

function displayValue(value: string): string {
  return value.trim() || "—";
}

type Props = {
  rightExtra?: React.ReactNode;
};

const AccountSidebar: React.FC<Props> = ({ rightExtra }) => {
  const { token, user, applyUser } = useAuth();
  const [profileSaving, setProfileSaving] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  const [fullName, setFullName] = useState("");
  const [company, setCompany] = useState("");
  const [phone, setPhone] = useState("");
  const [jobTitle, setJobTitle] = useState("");

  const [avatarUploading, setAvatarUploading] = useState(false);


  const [companyName, setCompanyName] = useState("");
  const [logoFilename, setLogoFilename] = useState("");
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [invoiceTemplateFilename, setInvoiceTemplateFilename] = useState("");
  const [invoiceTemplateUploading, setInvoiceTemplateUploading] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const invoiceTemplateInputRef = useRef<HTMLInputElement>(null);

  const [invoiceGstin, setInvoiceGstin] = useState("");
  const [invoiceAddress, setInvoiceAddress] = useState("");
  const [invoiceStateName, setInvoiceStateName] = useState("");
  const [invoiceStateCode, setInvoiceStateCode] = useState("");
  const [invoiceInfoSaving, setInvoiceInfoSaving] = useState(false);
  const [invoiceInfoIncomplete, setInvoiceInfoIncomplete] = useState(false);

  const [bankAccountHolder, setBankAccountHolder] = useState("");
  const [bankAccountNumber, setBankAccountNumber] = useState("");
  const [bankIfscCode, setBankIfscCode] = useState("");
  const [bankBranch, setBankBranch] = useState("");

  const [defaultTicketTemplate, setDefaultTicketTemplate] = useState<TicketTemplateId>("black");
  const [ticketTemplateSaving, setTicketTemplateSaving] = useState(false);

  const profileEdit = useSectionEditToggle();
  const ticketTemplateEdit = useSectionEditToggle();
  const invoiceInfoEdit = useSectionEditToggle();
  const { startEditing: startInvoiceInfoEditing } = invoiceInfoEdit;
  const { run } = useSubmitLock();

  useEffect(() => {
    if (!user) return;
    setFullName(user.full_name);
    setCompany(user.company ?? "");
    setPhone(user.phone ?? "");
    setJobTitle(user.job_title ?? "");
  }, [user]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    companiesApi
      .me(token)
      .then((companyData) => {
        if (cancelled) return;
        setCompanyName(companyData.name ?? "");
        setLogoFilename(companyData.logo_filename ?? "");
        setInvoiceTemplateFilename(companyData.invoice_template_filename ?? "");
        setInvoiceGstin(companyData.invoice_gstin ?? "");
        setInvoiceAddress(companyData.invoice_address ?? "");
        setInvoiceStateName(companyData.invoice_state_name ?? "");
        setInvoiceStateCode(companyData.invoice_state_code ?? "");
        setBankAccountHolder(companyData.bank_account_holder ?? "");
        setBankAccountNumber(companyData.bank_account_number ?? "");
        setBankIfscCode(companyData.bank_ifsc_code ?? "");
        setBankBranch(companyData.bank_branch ?? "");
        if (missingInvoiceInfo(companyData).length > 0) {
          setInvoiceInfoIncomplete(true);
          startInvoiceInfoEditing();
        }
        const templateId =
          companyData.default_ticket_template_id === "white" ? "white" : "black";
        setDefaultTicketTemplate(templateId);
        setCompanyDefaultTemplateIdLocal(templateId);
        if (companyData.logo_filename) {
          companiesApi
            .downloadLogo(token)
            .then((blob) => setLogoPreviewUrl(URL.createObjectURL(blob)))
            .catch(() => setLogoPreviewUrl(null));
        }
      })
      .catch(() => {
        if (cancelled) return;
        setCompanyName("");
        setLogoFilename("");
        setInvoiceTemplateFilename("");
        setDefaultTicketTemplate(getCompanyDefaultTemplateIdLocal());
      });
    return () => {
      cancelled = true;
    };
  }, [token, startInvoiceInfoEditing]);

  useEffect(() => {
    return () => {
      if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !token) return;
    await run(async () => {
      setAvatarUploading(true);
      try {
        const updated = await usersApi.uploadAvatar(file, token);
        applyUser(updated);
        toast.success("Avatar updated");
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Could not upload avatar");
      } finally {
        setAvatarUploading(false);
      }
    });
  };

  const handleSaveProfile = (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !user) return;
    void profileEdit.save(async () => {
      setProfileSaving(true);
      try {
        const updated = await usersApi.updateProfile(
          {
            full_name: fullName,
            company: company.trim() || null,
            phone: phone.trim() ? toE164(phone) ?? phone.trim() : null,
            job_title: jobTitle.trim() || null,
          },
          token
        );
        applyUser(updated);
        toast.success("Profile updated");
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Could not update profile");
        throw err;
      } finally {
        setProfileSaving(false);
      }
    });
  };

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !token) return;
    await run(async () => {
      setLogoUploading(true);
      try {
        const updated = await companiesApi.uploadLogo(file, token);
        setLogoFilename(updated.logo_filename ?? "");
        const blob = await companiesApi.downloadLogo(token);
        setLogoPreviewUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(blob);
        });
        toast.success("Logo updated");
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Could not upload logo");
      } finally {
        setLogoUploading(false);
      }
    });
  };

  const handleRemoveLogo = async () => {
    if (!token) return;
    await run(async () => {
      setLogoUploading(true);
      try {
        await companiesApi.deleteLogo(token);
        setLogoFilename("");
        setLogoPreviewUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return null;
        });
        toast.success("Logo removed");
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Could not remove logo");
      } finally {
        setLogoUploading(false);
      }
    });
  };

  const handleInvoiceTemplateChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !token) return;
    await run(async () => {
      setInvoiceTemplateUploading(true);
      try {
        const updated = await companiesApi.uploadInvoiceTemplate(file, token);
        setInvoiceTemplateFilename(updated.invoice_template_filename ?? "");
        toast.success("Invoice template updated");
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Could not upload invoice template");
      } finally {
        setInvoiceTemplateUploading(false);
      }
    });
  };

  const handleRemoveInvoiceTemplate = async () => {
    if (!token) return;
    await run(async () => {
      setInvoiceTemplateUploading(true);
      try {
        await companiesApi.deleteInvoiceTemplate(token);
        setInvoiceTemplateFilename("");
        toast.success("Invoice template removed");
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Could not remove invoice template");
      } finally {
        setInvoiceTemplateUploading(false);
      }
    });
  };

  const handleViewInvoiceTemplate = async () => {
    if (!token) return;
    try {
      const blob = await companiesApi.downloadInvoiceTemplate(token);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not open invoice template");
    }
  };

  const handleSaveInvoiceInfo = (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    const values = {
      gstin: invoiceGstin.trim().toUpperCase(),
      address: invoiceAddress.trim(),
      stateName: invoiceStateName.trim(),
      stateCode: invoiceStateCode.trim(),
      holder: bankAccountHolder.trim(),
      accountNumber: bankAccountNumber.trim(),
      ifsc: bankIfscCode.trim().toUpperCase(),
      branch: bankBranch.trim(),
    };
    if (invoiceInfoEdit.editing) {
      const missing = missingInvoiceInfo({
        invoice_address: values.address,
        invoice_state_name: values.stateName,
        invoice_state_code: values.stateCode,
        bank_account_holder: values.holder,
        bank_account_number: values.accountNumber,
        bank_ifsc_code: values.ifsc,
      });
      if (missing.length > 0) {
        toast.error(`Invoice information is required. Missing: ${missing.join(", ")}.`);
        return;
      }
      const formatError = invoiceInfoFormatError(values);
      if (formatError) {
        toast.error(formatError);
        return;
      }
    }
    void invoiceInfoEdit.save(async () => {
      setInvoiceInfoSaving(true);
      try {
        await companiesApi.update(
          {
            invoice_gstin: values.gstin || null,
            invoice_address: values.address,
            invoice_state_name: values.stateName,
            invoice_state_code: values.stateCode,
            bank_account_holder: values.holder,
            bank_account_number: values.accountNumber,
            bank_ifsc_code: values.ifsc,
            bank_branch: values.branch || null,
          },
          token
        );
        setInvoiceGstin(values.gstin);
        setInvoiceAddress(values.address);
        setInvoiceStateName(values.stateName);
        setInvoiceStateCode(values.stateCode);
        setBankAccountHolder(values.holder);
        setBankAccountNumber(values.accountNumber);
        setBankIfscCode(values.ifsc);
        setBankBranch(values.branch);
        setInvoiceInfoIncomplete(false);
        toast.success("Invoice information saved");
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Could not save invoice information");
        throw err;
      } finally {
        setInvoiceInfoSaving(false);
      }
    });
  };

  const handleSaveTicketTemplate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    void ticketTemplateEdit.save(async () => {
      setTicketTemplateSaving(true);
      setCompanyDefaultTemplateIdLocal(defaultTicketTemplate);
      try {
        await companiesApi.update({ default_ticket_template_id: defaultTicketTemplate }, token);
        toast.success("Default ticket template saved");
      } catch {
        toast.success("Default ticket template saved on this device");
      } finally {
        setTicketTemplateSaving(false);
      }
    });
  };

  if (!user) {
    return (
      <div className="eventos-account-card eventos-account-card--page">
        <div className="eventos-label">Account</div>
        <div className="eventos-subtle" style={{ marginTop: 6 }}>
          Profile will load from the server when GET <code className="eventos-code-inline">/users/me</code> is available, or sign in again after clearing storage.
        </div>
      </div>
    );
  }

  const avatarNode = user.avatar_url ? (
    <img className="eventos-account-avatar-img" src={user.avatar_url} alt="" />
  ) : (
    <div className="eventos-account-avatar">{avatarInitials(user)}</div>
  );

  return (
    <div className="eventos-profile-settings">
      <div className="eventos-profile-settings-grid">
        <div className="eventos-profile-settings-col">
          <form
            onSubmit={handleSaveProfile}
            className="eventos-form eventos-account-form-inner eventos-account-section eventos-account-section--profile-card"
          >
            <div className="eventos-label">User Profile</div>

            <div className="eventos-profile-user-row">
              <div className="eventos-profile-user-photo">
                {profileEdit.editing ? (
                  <label className="eventos-profile-photo-btn" title="Change photo">
                    {avatarNode}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={handleAvatarChange}
                      disabled={avatarUploading}
                      className="eventos-profile-photo-input"
                    />
                  </label>
                ) : (
                  avatarNode
                )}
              </div>

              <div className="eventos-profile-user-fields">
                {profileEdit.editing ? (
                  <>
                    <label className="eventos-field">
                      <span className="eventos-field-label">Name</span>
                      <input
                        className="eventos-input"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        disabled={!profileEdit.editing}
                        required
                      />
                    </label>
                    <label className="eventos-field">
                      <span className="eventos-field-label">Email</span>
                      <input className="eventos-input" value={user.email} disabled readOnly />
                    </label>
                    <label className="eventos-field">
                      <span className="eventos-field-label">Title</span>
                      <input
                        className="eventos-input"
                        value={jobTitle}
                        onChange={(e) => setJobTitle(e.target.value)}
                        disabled={!profileEdit.editing}
                        placeholder="Optional"
                      />
                    </label>
                    <label className="eventos-field">
                      <span className="eventos-field-label">Company</span>
                      <input
                        className="eventos-input"
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                        disabled={!profileEdit.editing}
                        placeholder="Optional"
                      />
                    </label>
                    <label className="eventos-field">
                      <span className="eventos-field-label">Phone number</span>
                      <input
                        className="eventos-input"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        disabled={!profileEdit.editing}
                        placeholder="+91 98765 43210"
                        type="tel"
                        autoComplete="tel"
                      />
                    </label>
                  </>
                ) : (
                  <dl className="eventos-profile-readonly-list eventos-profile-readonly-list--stacked">
                    <div>
                      <dt>Name</dt>
                      <dd>{displayValue(fullName)}</dd>
                    </div>
                    <div>
                      <dt>Email</dt>
                      <dd>{displayValue(user.email)}</dd>
                    </div>
                    <div>
                      <dt>Title</dt>
                      <dd>{displayValue(jobTitle)}</dd>
                    </div>
                    <div>
                      <dt>Company</dt>
                      <dd>{displayValue(company)}</dd>
                    </div>
                    <div>
                      <dt>Phone number</dt>
                      <dd>{displayValue(phone)}</dd>
                    </div>
                  </dl>
                )}
              </div>
            </div>

            <div className="eventos-account-section-actions eventos-profile-user-actions">
              <button
                className="eventos-nav-btn eventos-account-section-btn"
                type="button"
                onClick={() => setChangePasswordOpen(true)}
              >
                Change password
              </button>
              <button className="eventos-nav-btn eventos-account-section-btn" type="submit" disabled={profileSaving}>
                {profileSaving ? "Saving…" : profileEdit.editing ? "Save" : "Edit profile"}
              </button>
            </div>
          </form>

          <div className="eventos-form eventos-account-form-inner eventos-account-section eventos-account-section--profile-card">
            <div className="eventos-label">Company settings</div>

            <div className="eventos-field">
              <span className="eventos-field-label">Logo</span>
              <div className="eventos-profile-asset-row">
                {logoPreviewUrl ? (
                  <img src={logoPreviewUrl} alt="Company logo" className="eventos-profile-asset-thumb" />
                ) : null}
                <span className="eventos-profile-asset-value">{logoFilename || "No logo uploaded"}</span>
                <div className="eventos-profile-asset-actions">
                  <button
                    type="button"
                    className="eventos-nav-btn"
                    onClick={() => logoInputRef.current?.click()}
                    disabled={logoUploading}
                  >
                    {logoUploading ? "Uploading…" : logoFilename ? "Replace" : "Upload"}
                  </button>
                  {logoFilename ? (
                    <button
                      type="button"
                      className="eventos-nav-btn"
                      onClick={() => void handleRemoveLogo()}
                      disabled={logoUploading}
                    >
                      Remove
                    </button>
                  ) : null}
                </div>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  hidden
                  onChange={(e) => void handleLogoChange(e)}
                />
              </div>
              <span className="eventos-subtle">PNG, JPEG, or WebP</span>
            </div>

            <div className="eventos-field">
              <span className="eventos-field-label">Invoice template</span>
              <div className="eventos-profile-asset-row">
                <span className="eventos-profile-asset-value">{invoiceTemplateFilename || "No document uploaded"}</span>
                <div className="eventos-profile-asset-actions">
                  {invoiceTemplateFilename ? (
                    <button
                      type="button"
                      className="eventos-nav-btn"
                      onClick={() => void handleViewInvoiceTemplate()}
                    >
                      View
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="eventos-nav-btn"
                    onClick={() => invoiceTemplateInputRef.current?.click()}
                    disabled={invoiceTemplateUploading}
                  >
                    {invoiceTemplateUploading ? "Uploading…" : invoiceTemplateFilename ? "Replace" : "Upload"}
                  </button>
                  {invoiceTemplateFilename ? (
                    <button
                      type="button"
                      className="eventos-nav-btn"
                      onClick={() => void handleRemoveInvoiceTemplate()}
                      disabled={invoiceTemplateUploading}
                    >
                      Remove
                    </button>
                  ) : null}
                </div>
                <input
                  ref={invoiceTemplateInputRef}
                  type="file"
                  accept="image/png,image/jpeg,application/pdf"
                  hidden
                  onChange={(e) => void handleInvoiceTemplateChange(e)}
                />
              </div>
              <span className="eventos-subtle">PNG, JPEG, or PDF</span>
            </div>
          </div>

          <form
            onSubmit={handleSaveTicketTemplate}
            className="eventos-form eventos-account-form-inner eventos-account-section eventos-account-section--profile-card"
          >
            <div className="eventos-label">Default RSVP / Guest List template</div>
            <p className="eventos-subtle" style={{ marginTop: 0 }}>
              Choose the black or white signup template for new events. Override per event in RSVP → Share → View template.
            </p>
            <DefaultTemplatePreview
              templateId={defaultTicketTemplate}
              editing={ticketTemplateEdit.editing}
              onSelect={setDefaultTicketTemplate}
            />
            <button
              className="eventos-nav-btn eventos-account-section-btn eventos-profile-card-btn"
              type="submit"
              disabled={ticketTemplateSaving}
            >
              {ticketTemplateSaving ? "Saving…" : ticketTemplateEdit.editing ? "Save" : "Edit"}
            </button>
          </form>

          {token ? <NotificationPreferencesCard token={token} /> : null}
        </div>

        <div className="eventos-profile-settings-col eventos-profile-settings-col--scroll">
          <form
            onSubmit={handleSaveInvoiceInfo}
            className="eventos-form eventos-account-form-inner eventos-account-section eventos-account-section--profile-card"
          >
            <div className="eventos-label">Invoice information</div>
            {invoiceInfoIncomplete ? (
              <p className="eventos-profile-notice" role="status">
                Invoice information is required before you can generate invoices. Add your address, state and bank
                details below.
              </p>
            ) : null}

            <label className="eventos-field">
              <span className="eventos-field-label">Name</span>
              <input className="eventos-input" value={companyName} disabled readOnly />
            </label>
            <label className="eventos-field">
              <span className="eventos-field-label">Address</span>
              <input
                className="eventos-input"
                value={invoiceAddress}
                onChange={(e) => setInvoiceAddress(e.target.value)}
                disabled={!invoiceInfoEdit.editing}
                placeholder="Registered business address"
                autoComplete="street-address"
                required
              />
            </label>
            <label className="eventos-field">
              <span className="eventos-field-label">GSTIN</span>
              <input
                className="eventos-input"
                value={invoiceGstin}
                onChange={(e) => setInvoiceGstin(e.target.value.toUpperCase())}
                disabled={!invoiceInfoEdit.editing}
                placeholder="Optional — leave blank if not GST-registered"
                maxLength={15}
              />
            </label>
            <div className="eventos-profile-2col">
              <label className="eventos-field">
                <span className="eventos-field-label">State name</span>
                <input
                  className="eventos-input"
                  value={invoiceStateName}
                  onChange={(e) => setInvoiceStateName(e.target.value)}
                  disabled={!invoiceInfoEdit.editing}
                  placeholder="e.g. Maharashtra"
                  required
                />
              </label>
              <label className="eventos-field">
                <span className="eventos-field-label">State code</span>
                <input
                  className="eventos-input"
                  value={invoiceStateCode}
                  onChange={(e) => setInvoiceStateCode(e.target.value.replace(/\D/g, "").slice(0, 2))}
                  disabled={!invoiceInfoEdit.editing}
                  placeholder="e.g. 27"
                  inputMode="numeric"
                  required
                />
              </label>
            </div>

            <div className="eventos-profile-subheading">Bank details</div>
            <div className="eventos-profile-kv-grid">
              <label className="eventos-field">
                <span className="eventos-field-label">Account holder</span>
                <input
                  className="eventos-input"
                  value={bankAccountHolder}
                  onChange={(e) => setBankAccountHolder(e.target.value)}
                  disabled={!invoiceInfoEdit.editing}
                  placeholder="Name as on bank account"
                  required
                />
              </label>
              <label className="eventos-field">
                <span className="eventos-field-label">Account number</span>
                <input
                  className="eventos-input"
                  value={bankAccountNumber}
                  onChange={(e) => setBankAccountNumber(e.target.value.replace(/\D/g, "").slice(0, 18))}
                  disabled={!invoiceInfoEdit.editing}
                  placeholder="9–18 digits"
                  inputMode="numeric"
                  required
                />
              </label>
              <label className="eventos-field">
                <span className="eventos-field-label">IFSC code</span>
                <input
                  className="eventos-input"
                  value={bankIfscCode}
                  onChange={(e) => setBankIfscCode(e.target.value.toUpperCase().slice(0, 11))}
                  disabled={!invoiceInfoEdit.editing}
                  placeholder="e.g. HDFC0001234"
                  required
                />
              </label>
              <label className="eventos-field">
                <span className="eventos-field-label">Branch</span>
                <input
                  className="eventos-input"
                  value={bankBranch}
                  onChange={(e) => setBankBranch(e.target.value)}
                  disabled={!invoiceInfoEdit.editing}
                  placeholder="Optional"
                />
              </label>
            </div>

            <button className="eventos-nav-btn eventos-account-section-btn eventos-profile-card-btn" type="submit" disabled={invoiceInfoSaving}>
              {invoiceInfoSaving ? "Saving…" : invoiceInfoEdit.editing ? "Save" : "Edit"}
            </button>
          </form>

          {rightExtra}
        </div>
      </div>

      {changePasswordOpen ? (
        <ChangePasswordModal token={token} onClose={() => setChangePasswordOpen(false)} />
      ) : null}
    </div>
  );
};

export default AccountSidebar;
